import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { IDisplayLogicAttribute, IHrisAttributeModel, IPersonAttribute } from '../models';
import { TFetchableEntity } from '../models/IFetchableEntity';
import * as HierarchyAPI from '../services/HierarchyAPI';
import * as PersonAttributeAPI from '../services/PersonAttributeAPI';
import { IApplicationState } from './';

interface IBranchingLogicAttributesState {
	attributes?: TFetchableEntity<IDisplayLogicAttribute[]>;
}

const initialState: IBranchingLogicAttributesState = {};

const fetchHrisAttributes = createAsyncThunk('branchingLogicHrisAttributes/fetchHrisAttributes', async (hrisId: string) => {
	return HierarchyAPI.getHrisAttributes(hrisId);
});

const fetchHrisAttributeValues = createAsyncThunk(
	'branchingLogicHrisAttributes/fetchHrisAttributeValues',
	async (hrisId: string, { getState }) => {
		const attributes = (getState() as IApplicationState).questionBranchingLogicAttributes.attributes;

		let valuesByAttribute: { [key: string]: string[] } = {};
		if (attributes && attributes instanceof Array) {
			for (const attr of attributes) {
				if (attr.values.length === 0) {
					const values = await HierarchyAPI.getHrisAttributeValues(hrisId, attr.name);
					const attrValues = values.map((a) => a.value).filter((v): v is string => !!v);
					valuesByAttribute = { ...valuesByAttribute, [attr.name]: attrValues };
				}
			}
		}
		return valuesByAttribute;
	},
);

const fetchPersonAttributes = createAsyncThunk(
	'branchingLogicHrisAttributes/fetchPersonAttributes',
	async (props: { surveyId: number; clientId: number }, { getState }) => {
		const { surveyId, clientId } = props;
		const { audiences } = (getState() as IApplicationState).audienceList;
		return Promise.all(audiences.map((aud) => PersonAttributeAPI.getPersonAttributes(surveyId, aud.audienceId, clientId)));
	},
);

const branchingLogicHrisAttributesSlice = createSlice({
	name: 'branchingLogicHrisAttributes',
	initialState: initialState,
	reducers: {
		resetAttributes: () => {
			return { ...initialState };
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchHrisAttributes.fulfilled, (state, action) => {
				const attributes = action.payload.map((attr: IHrisAttributeModel) => ({
					...attr,
					displayName: attr.name,
					values: [],
				}));
				const attributesList = attributes as IDisplayLogicAttribute[];

				state.attributes = attributesList;
			})
			.addCase(fetchHrisAttributeValues.fulfilled, (state, action) => {
				if (state.attributes && !state.attributes.isLoading && !state.attributes.couldNotFetchData) {
					for (const attr of state.attributes as IDisplayLogicAttribute[]) {
						if (attr) {
							attr.values = action.payload[attr.name];
						}
					}
				}
			})
			.addCase(fetchPersonAttributes.fulfilled, (state, action) => {
				const attrs = action.payload.reduce((branchingLogicAttrs, personAttrs) => {
					personAttrs.forEach((attr) => {
						const attrValues = getAttributeValues(action.payload, attr);

						const branchingLogicAttr: IDisplayLogicAttribute = {
							name: attr.attributeName,
							displayName: attr.attributeDisplayName ?? '',
							type: 'string',
							columnIndex: attr.attributeId,
							values: attrValues,
						};

						branchingLogicAttrs.set(attr.attributeId.toString(), branchingLogicAttr);
					});

					return branchingLogicAttrs;
				}, new Map<string, IDisplayLogicAttribute>());

				const attributes = Array.from(attrs.values());

				state.attributes = attributes;
			})
			.addMatcher(isAnyOf(fetchHrisAttributes.pending, fetchPersonAttributes.pending), (state) => {
				state.attributes = { isLoading: true };
			})
			.addMatcher(isAnyOf(fetchHrisAttributes.rejected, fetchPersonAttributes.rejected), (state) => {
				state.attributes = { isLoading: false, couldNotFetchData: true };
			});
	},
});

const getAttributeValues = (payload: IPersonAttribute[][], attribute: IPersonAttribute) => {
	const attrValueSet = payload.reduce((attrValues, attrs) => {
		attrs.forEach((attr) => {
			if (attr.attributeName === attribute.attributeName && attr.value) {
				attrValues.add(attr.value);
			}
		});

		return attrValues;
	}, new Set<string>());

	return Array.from(attrValueSet);
};

export { fetchHrisAttributes, fetchHrisAttributeValues, fetchPersonAttributes, type IBranchingLogicAttributesState, initialState };
export const { resetAttributes } = branchingLogicHrisAttributesSlice.actions;
export default branchingLogicHrisAttributesSlice.reducer;
